<template>
  <section class="management-section" data-testid="management-blockages">
    <ManagementSectionHeader :title="$t('UserProjects.management.blockages.title')" />

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div class="management-panel-card p-5">
        <p class="text-xs font-medium text-[var(--color-dark)] mb-3 m-0">
          {{ $t('UserProjects.management.blockages.recurringCauses') }}
        </p>

        <div v-if="recurringCauses.length === 0" class="text-sm text-[var(--color-disabled)]">
          {{ $t('UserProjects.management.blockages.emptyCauses') }}
        </div>

        <div v-else class="flex flex-col gap-2">
          <div
            v-for="row in recurringCauses"
            :key="row.label"
            class="flex items-center gap-3"
            :data-testid="`management-cause-row-${slugify(row.label)}`"
          >
            <span class="text-xs text-[var(--color-dark)] flex-1 min-w-0 truncate">{{ row.label }}</span>
            <div class="flex-1 h-2 rounded-full bg-[var(--color-light-grey)] overflow-hidden max-w-[120px]">
              <div
                class="h-full rounded-full"
                :style="{
                  width: `${barWidth(row.count, maxCauseCount)}%`,
                  backgroundColor: causeBarColor(row.label),
                }"
              />
            </div>
            <span class="text-xs font-bold text-[var(--color-dark)] w-6 text-right">{{ row.count }}</span>
            <span class="text-[11px] text-[var(--color-disabled)] w-16 text-right shrink-0">
              {{ row.projectCount }} {{ $t('UserProjects.management.blockages.projectsSuffix') }}
            </span>
          </div>
        </div>
      </div>

      <div class="management-panel-card p-5">
        <p class="text-xs font-medium text-[var(--color-dark)] mb-3 m-0">
          {{ $t('UserProjects.management.blockages.responsibilityAreas') }}
        </p>

        <div v-if="responsibilityAreas.length === 0" class="text-sm text-[var(--color-disabled)]">
          {{ $t('UserProjects.management.blockages.emptyAreas') }}
        </div>

        <div v-else class="flex flex-col gap-0">
          <div
            v-for="row in responsibilityAreas"
            :key="row.label"
            class="flex items-center gap-3 py-1.5 border-b border-[var(--color-light-grey)] last:border-b-0"
            :data-testid="`management-area-row-${slugify(row.label)}`"
          >
            <span class="text-xs text-[var(--color-dark)] flex-1">{{ row.label }}</span>
            <span
              class="text-xs font-bold whitespace-nowrap"
              :style="{ color: responsibilityBlockadeColor(row.blockadeCount) }"
            >
              {{ row.blockadeCount }} {{ $t('UserProjects.management.blockages.blockadesSuffix') }}
            </span>
            <span class="text-[11px] text-[var(--color-disabled)] whitespace-nowrap">
              {{ row.projectCount }} {{ $t('UserProjects.management.blockages.projectsSuffix') }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import ManagementSectionHeader from '@/components/CustomerApp/UserProjects/ManagementSectionHeader.vue';
import { causeBarColor, responsibilityBlockadeColor } from '@/utils/managementDisplay';

export default {
  name: 'ManagementBlockagesPanel',
  components: { ManagementSectionHeader },
  props: {
    blockages: {
      type: Object,
      default: () => ({ recurringCauses: [], responsibilityAreas: [] }),
    },
  },
  computed: {
    recurringCauses() {
      return this.blockages?.recurringCauses ?? [];
    },
    responsibilityAreas() {
      return this.blockages?.responsibilityAreas ?? [];
    },
    maxCauseCount() {
      return Math.max(...this.recurringCauses.map((row) => row.count), 1);
    },
  },
  methods: {
    causeBarColor,
    responsibilityBlockadeColor,
    barWidth(count, max) {
      return Math.max(8, Math.round((count / max) * 100));
    },
    slugify(value) {
      return String(value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    },
  },
};
</script>

<style scoped>
@import './management-section.css';
</style>
